/**
 * ╔══════════════════════════════════════════════════════════════════╗
 * ║  NAV REGISTRY — Single source of truth for menu + RBAC subjects  ║
 * ║                                                                  ║
 * ║  The hierarchical tree below drives BOTH:                        ║
 * ║   1. The sidebar menu (UnifiedSidebar)                           ║
 * ║   2. The RBAC subject space (role_abilities.subject)             ║
 * ║                                                                  ║
 * ║  Subject ids are dotted paths: "ops.crm.customers". Granting an  ║
 * ║  ability on an ancestor cascades to all descendants — the same   ║
 * ║  way "manage" cascades to view/create/update/delete.             ║
 * ║                                                                  ║
 * ║  Adding a menu item = adding a permission slot. They cannot      ║
 * ║  drift because they share this file.                             ║
 * ╚══════════════════════════════════════════════════════════════════╝
 */
import type { LucideIcon } from "lucide-react";
import {
  Archive, AlertTriangle, BarChart3, Bell, BookOpen, Briefcase, Building,
  CalendarCheck, Clock, ClipboardList, Contact, CreditCard, Database,
  DollarSign, Factory, FileSignature, FileText, Gift, GitBranch, Globe,
  GraduationCap, Handshake, Headset, Inbox, Layers, LayoutDashboard,
  LayoutGrid, LifeBuoy, Link2Off, List, Mail, MapPin, MessageCircle,
  MessageSquare, Newspaper, Plug, RefreshCw, Rocket, Route, ScanLine,
  Settings, ShieldAlert, Sparkles, Store, Tags, Target, TrendingUp,
  Trash2, Truck, UserCheck, UserPlus, UserX, Users, XCircle, Zap, PhoneCall,
  type LucideIcon as _LI,
} from "lucide-react";
import type { ActionId } from "@/types/abilities";

export type DashboardId = "ops" | "bizdev" | "billing" | "performance" | "helpdesk";

export interface NavNode {
  /** Dotted path id (e.g. "ops.crm.customers"). Also the RBAC subject. */
  id: string;
  label: string;
  icon?: LucideIcon;
  iconColor?: string;
  /** Tab id consumed by Backoffice/dashboard pages. Leaf nodes typically have one. */
  route?: string;
  description?: string;
  /** Extra non-CRUD actions meaningful for this node (e.g. "approve" on MSA). */
  actions?: ActionId[];
  defaultCollapsed?: boolean;
  children?: NavNode[];
}

// ─── Operations ────────────────────────────────────────────────────
const opsTree: NavNode = {
  id: "ops",
  label: "Operations",
  description: "Backoffice operations workspace",
  children: [
    {
      id: "ops.overview", label: "Overview",
      children: [
        { id: "ops.overview.dashboard", label: "Dashboard", icon: LayoutDashboard, route: "reporting" },
        { id: "ops.overview.am-dashboard", label: "AM Dashboard", icon: BarChart3, route: "am-dashboard" },
        { id: "ops.overview.assistant", label: "AI Assistant", icon: Sparkles, iconColor: "text-[#E31C1A]", route: "assistant" },
      ],
    },
    {
      id: "ops.crm", label: "CRM",
      description: "Customer relationship records",
      children: [
        { id: "ops.crm.contacts", label: "Contacts", icon: Users, iconColor: "text-blue-500", route: "contacts" },
        { id: "ops.crm.customers", label: "Customers", icon: Building, iconColor: "text-indigo-500", route: "customers-list", actions: ["export"] },
        { id: "ops.crm.strategy", label: "Strategy", icon: Target, iconColor: "text-violet-500", route: "strategy" },
        { id: "ops.crm.msa", label: "MSA", icon: FileText, iconColor: "text-amber-500", route: "submissions", actions: ["approve", "export"] },
      ],
    },
    {
      id: "ops.production", label: "Production",
      children: [
        { id: "ops.production.onboarding", label: "Onboarding", icon: UserCheck, iconColor: "text-emerald-500", route: "onboarding" },
        { id: "ops.production.hydra-tracker", label: "Hydra Tracker", icon: CalendarCheck, iconColor: "text-orange-500", route: "tracking" },
        { id: "ops.production.hydra-changes", label: "Hydra Changes", icon: Layers, iconColor: "text-sky-400", route: "hydra-changeouts-ops" },
        { id: "ops.production.domains", label: "Domains", icon: Globe, iconColor: "text-sky-500", route: "domains" },
        { id: "ops.production.integrations", label: "Integrations", icon: Plug, iconColor: "text-purple-500", route: "integrations" },
      ],
    },
    {
      id: "ops.partners", label: "Partners",
      children: [
        { id: "ops.partners.equity", label: "Equity Partners", icon: Handshake, iconColor: "text-violet-400", route: "equity-partners" },
        { id: "ops.partners.ppl", label: "Pay Per Lead", icon: DollarSign, iconColor: "text-teal-400", route: "ppl-partners" },
      ],
    },
    {
      id: "ops.retention", label: "Retention",
      children: [
        { id: "ops.retention.plan-changes", label: "Plan Changes", icon: FileText, iconColor: "text-amber-500", route: "plan-changes" },
        { id: "ops.retention.cancellations", label: "Cancellations", icon: XCircle, iconColor: "text-red-400", route: "cancellations", actions: ["approve"] },
      ],
    },
    {
      id: "ops.admin", label: "Admin", defaultCollapsed: true,
      description: "Platform administration",
      children: [
        { id: "ops.admin.users", label: "Users", icon: Users, route: "users", actions: ["impersonate"] },
        { id: "ops.admin.rbac", label: "RBAC", icon: ShieldAlert, iconColor: "text-amber-400", route: "rbac" },
        { id: "ops.admin.project-roles", label: "Project Roles", icon: UserCheck, iconColor: "text-blue-400", route: "project-roles" },
        { id: "ops.admin.notifications", label: "Notifications", icon: Bell, iconColor: "text-amber-400", route: "notification-routing" },
        { id: "ops.admin.product-catalog", label: "Product Catalog", icon: Store, route: "product-catalog" },
        { id: "ops.admin.training", label: "Training", icon: BookOpen, route: "training" },
        { id: "ops.admin.system-training", label: "System Training", icon: GraduationCap, iconColor: "text-[#E31C1A]", route: "system-training" },
        { id: "ops.admin.whats-new", label: "What's New on Hydra", icon: Sparkles, iconColor: "text-accent", route: "whats-new" },
        { id: "ops.admin.hydra-roadmap", label: "Future of Hydra", icon: Sparkles, iconColor: "text-accent", route: "hydra-roadmap" },
        { id: "ops.admin.reminders", label: "Reminders", icon: Clock, route: "reminder-logs" },
        { id: "ops.admin.deletion-log", label: "Deletion Log", icon: Trash2, route: "deletion-log" },
      ],
    },
  ],
};

// ─── Billing ───────────────────────────────────────────────────────
const billingTree: NavNode = {
  id: "billing", label: "Billing",
  children: [
    {
      id: "billing.overview", label: "Overview",
      children: [
        { id: "billing.overview.dashboard", label: "Financial Dashboard", icon: LayoutDashboard, iconColor: "text-accent", route: "dashboard" },
        { id: "billing.overview.actions", label: "Billing Actions", icon: FileText, iconColor: "text-emerald-500", route: "billing-actions", actions: ["approve"] },
      ],
    },
    {
      id: "billing.finance", label: "Finance",
      children: [
        { id: "billing.finance.hub", label: "Billing Hub", icon: DollarSign, iconColor: "text-green-500", route: "billing-hub" },
        { id: "billing.finance.invoices", label: "Invoices & AR", icon: CreditCard, iconColor: "text-lime-500", route: "billing-invoices", actions: ["export"] },
        { id: "billing.finance.subscriptions", label: "Subscriptions", icon: BarChart3, iconColor: "text-indigo-400", route: "subscriptions" },
      ],
    },
    {
      id: "billing.reporting", label: "Reporting/Audit",
      children: [
        { id: "billing.reporting.signed-msa-report", label: "Signed MSA Report", icon: FileText, iconColor: "text-amber-400", route: "signed-msa-report", actions: ["export", "approve"] },
        { id: "billing.reporting.onepath-subscribers", label: "OnePath Subscribers", icon: BarChart3, iconColor: "text-cyan-400", route: "onepath-subscribers", actions: ["export"] },
        { id: "billing.reporting.ai-local-subscribers", label: "AI Local Subscribers", icon: BarChart3, iconColor: "text-sky-400", route: "ai-local-subscribers", actions: ["export"] },
        { id: "billing.reporting.blogging-subscribers", label: "Blogging Subscribers", icon: BarChart3, iconColor: "text-emerald-400", route: "blogging-subscribers", actions: ["export"] },
        { id: "billing.reporting.link-building-subscribers", label: "Link Building Subscribers", icon: BarChart3, iconColor: "text-purple-400", route: "link-building-subscribers", actions: ["export"] },
        { id: "billing.reporting.tech-credits-subscribers", label: "AI Technical Credits", icon: BarChart3, iconColor: "text-pink-400", route: "tech-credits-subscribers", actions: ["export"] },
        { id: "billing.reporting.press-release-subscribers", label: "Press Release Subscribers", icon: BarChart3, iconColor: "text-orange-400", route: "press-release-subscribers", actions: ["export"] },
        { id: "billing.reporting.strategy-sessions-subscribers", label: "Strategy Sessions Subscribers", icon: BarChart3, iconColor: "text-teal-400", route: "strategy-sessions-subscribers", actions: ["export"] },
        { id: "billing.reporting.roas-subscribers", label: "SearchLight ROAS Subscribers", icon: BarChart3, iconColor: "text-rose-400", route: "roas-subscribers", actions: ["export"] },
        { id: "billing.reporting.hydra-website-content", label: "Hydra Website + Content", icon: BarChart3, iconColor: "text-blue-400", route: "hydra-website-content", actions: ["export"] },
        { id: "billing.reporting.branding-subscribers", label: "Branding Subscribers", icon: BarChart3, iconColor: "text-fuchsia-400", route: "branding-subscribers", actions: ["export"] },
        { id: "billing.reporting.one-time-addons", label: "One-Time Investment Add-Ons", icon: BarChart3, iconColor: "text-yellow-400", route: "one-time-addons", actions: ["export"] },
        { id: "billing.reporting.ppl-partners-billing", label: "Pay-Per-Lead Partners", icon: PhoneCall, iconColor: "text-teal-400", route: "ppl-partners-billing", actions: ["export"] },
      ],
    },
    {
      id: "billing.retention", label: "Retention",
      children: [
        { id: "billing.retention.plan-changes", label: "Plan Changes", icon: FileText, iconColor: "text-amber-500", route: "plan-changes" },
        { id: "billing.retention.cancellations", label: "Cancellations", icon: XCircle, iconColor: "text-red-400", route: "cancellations", actions: ["approve"] },
      ],
    },
  ],
};

// ─── BizDev ────────────────────────────────────────────────────────
const bizdevTree: NavNode = {
  id: "bizdev", label: "Business Development",
  children: [
    {
      id: "bizdev.overview", label: "Overview",
      children: [
        { id: "bizdev.overview.dashboard", label: "Dashboard", icon: LayoutDashboard, route: "dashboard" },
        { id: "bizdev.overview.sales-performance", label: "Sales Performance", icon: TrendingUp, route: "sales-performance" },
      ],
    },
    {
      id: "bizdev.activity", label: "Sales Activity",
      children: [
        { id: "bizdev.activity.my-tasks", label: "My Follow-Ups", icon: ClipboardList, route: "my-sales-tasks" },
        { id: "bizdev.activity.team-board", label: "Team Activity", icon: LayoutGrid, route: "sales-task-board" },
        { id: "bizdev.activity.calendar", label: "Calendar", icon: CalendarCheck, route: "sales-calendar" },
      ],
    },
    {
      id: "bizdev.marketing", label: "Marketing",
      children: [
        { id: "bizdev.marketing.campaigns", label: "Campaigns", icon: CalendarCheck, route: "campaigns" },
        { id: "bizdev.marketing.sources", label: "Lead Sources", icon: GitBranch, route: "sources" },
        { id: "bizdev.marketing.tsm-callbacks", label: "TSM Callbacks", icon: Users, route: "tsm-callbacks" },
        { id: "bizdev.marketing.outbound", label: "Outbound", icon: Target, route: "outbound" },
        { id: "bizdev.marketing.performance", label: "Performance", icon: TrendingUp, route: "performance" },
        { id: "bizdev.marketing.legacy-pipeline", label: "Legacy Pipeline", icon: Briefcase, route: "legacy-pipeline" },
      ],
    },
    {
      id: "bizdev.pipeline", label: "Sales Pipeline",
      children: [
        { id: "bizdev.pipeline.leads-db", label: "Leads", icon: Database, route: "leads-db", actions: ["export", "assign"] },
        { id: "bizdev.pipeline.prospects", label: "All Prospects", icon: UserPlus, route: "leads" },
        { id: "bizdev.pipeline.tsm-reps", label: "TSM Referrals", icon: Users, route: "tsm-reps" },
        { id: "bizdev.pipeline.opportunities", label: "Open Opportunities", icon: Sparkles, route: "open-opportunities" },
        { id: "bizdev.pipeline.contacts", label: "Contacts", icon: Contact, route: "contacts" },
        { id: "bizdev.pipeline.evaluations", label: "Website Evaluations", icon: Globe, route: "website-evaluations" },
        { id: "bizdev.pipeline.analyzer", label: "Website Analyzer", icon: ScanLine, route: "website-analyzer" },
        { id: "bizdev.pipeline.chat-logs", label: "Chat Logs", icon: MessageCircle, route: "chat-logs" },
        { id: "bizdev.pipeline.proposals", label: "Custom Proposals", icon: FileSignature, route: "proposals", actions: ["approve"] },
        { id: "bizdev.pipeline.email-templates", label: "Email Templates", icon: Mail, route: "email-templates" },
        { id: "bizdev.pipeline.email-log", label: "Email Audit Log", icon: Mail, route: "email-log" },
        { id: "bizdev.pipeline.helpdesk", label: "Helpdesk", icon: LifeBuoy, route: "helpdesk" },
      ],
    },
    {
      id: "bizdev.forms", label: "Forms",
      children: [
        { id: "bizdev.forms.media-requests", label: "Media Requests", icon: Newspaper, route: "media-requests" },
        { id: "bizdev.forms.quote-requests", label: "Quote Requests", icon: FileText, route: "requests" },
      ],
    },
    {
      id: "bizdev.partners", label: "Partners",
      children: [
        { id: "bizdev.partners.vendors", label: "Vendor Partners", icon: Handshake, route: "strategic-partners" },
        { id: "bizdev.partners.resellers", label: "Resellers", icon: Store, route: "resellers" },
        { id: "bizdev.partners.outsourcing", label: "Outsourcing Partners", icon: Handshake, route: "partners" },
      ],
    },
    {
      id: "bizdev.database", label: "Database",
      children: [
        { id: "bizdev.database.manufacturers", label: "Manufacturers", icon: Factory, route: "manufacturers" },
        { id: "bizdev.database.distributors", label: "Distributors", icon: Database, route: "distributor-database" },
        { id: "bizdev.database.franchises", label: "Franchises", icon: Store, route: "franchise-database" },
        { id: "bizdev.database.pe-firms", label: "Private Equity", icon: Briefcase, route: "pe-firms" },
        { id: "bizdev.database.promo-codes", label: "Partner Codes", icon: Gift, route: "promo-codes" },
      ],
    },
    {
      id: "bizdev.archives", label: "Archives", defaultCollapsed: true,
      children: [
        { id: "bizdev.archives.completed-events", label: "Completed Events", icon: CalendarCheck, route: "completed-events" },
        { id: "bizdev.archives.reengagement", label: "Re-Engagement", icon: RefreshCw, route: "reengagement" },
      ],
    },
    {
      id: "bizdev.zoho", label: "Zoho Migration", defaultCollapsed: true,
      children: [
        { id: "bizdev.zoho.customers", label: "Active Customers", icon: Building, route: "zoho-customers" },
        { id: "bizdev.zoho.cancelled", label: "Cancelled", icon: UserX, route: "zoho-cancelled" },
        { id: "bizdev.zoho.leads", label: "Leads", icon: Users, route: "zoho-leads" },
        { id: "bizdev.zoho.contacts", label: "Contacts", icon: Users, route: "zoho-contacts" },
        { id: "bizdev.zoho.deals", label: "Deals", icon: Handshake, route: "zoho-deals" },
        { id: "bizdev.zoho.vendors", label: "Vendors", icon: Store, route: "zoho-vendors" },
        { id: "bizdev.zoho.distributors", label: "Distributors", icon: Truck, route: "zoho-distributors" },
        { id: "bizdev.zoho.manufacturers", label: "Manufacturers", icon: Factory, route: "zoho-manufacturers" },
      ],
    },
  ],
};

// ─── Performance ───────────────────────────────────────────────────
const performanceTree: NavNode = {
  id: "performance", label: "Performance",
  children: [
    { id: "performance.overview", label: "Overview", children: [
      { id: "performance.overview.dashboard", label: "Dashboard", icon: LayoutDashboard, route: "dashboard" },
    ]},
    { id: "performance.channels", label: "Channels", children: [
      { id: "performance.channels.ai-local", label: "AI Local", icon: MapPin, iconColor: "text-cyan-500", route: "marketing-ai-local" },
      { id: "performance.channels.cortex", label: "Cortex", icon: Globe, iconColor: "text-teal-500", route: "marketing-seo-aeo" },
      { id: "performance.channels.pulse", label: "Pulse", icon: Zap, iconColor: "text-yellow-500", route: "marketing-pulse" },
      { id: "performance.channels.subscriptions", label: "Subscriptions", icon: BarChart3, iconColor: "text-indigo-400", route: "marketing-subscriptions" },
    ]},
    { id: "performance.my-work", label: "My Work", children: [
      { id: "performance.my-work.tasks", label: "My Tasks", icon: ClipboardList, route: "my-tasks" },
      { id: "performance.my-work.schedule", label: "Schedule", icon: CalendarCheck, route: "my-schedule" },
    ]},
    { id: "performance.tasks", label: "Task Management", children: [
      { id: "performance.tasks.board", label: "Task Board", icon: LayoutGrid, route: "task-board" },
      { id: "performance.tasks.list", label: "All Tasks", icon: List, route: "task-list" },
      { id: "performance.tasks.hydra-sites", label: "Hydra Sites", icon: Globe, route: "hydra-sites" },
    ]},
    { id: "performance.department", label: "Department", children: [
      { id: "performance.department.team", label: "Team Performance", icon: Target, route: "dept-performance" },
      { id: "performance.department.goals", label: "Department Goals", icon: Rocket, route: "dept-goals" },
    ]},
    { id: "performance.reports", label: "Reports", defaultCollapsed: true, children: [
      { id: "performance.reports.compliance", label: "Task Compliance", icon: BarChart3, route: "compliance" },
      { id: "performance.reports.workload", label: "Workload", icon: Users, route: "workload" },
    ]},
    { id: "performance.settings", label: "Settings", defaultCollapsed: true, children: [
      { id: "performance.settings.service-types", label: "Service Types & Tasks", icon: Settings, route: "service-types" },
      { id: "performance.settings.milestones", label: "Milestone Timelines", icon: Clock, route: "milestone-timelines" },
    ]},
  ],
};

// ─── Helpdesk ──────────────────────────────────────────────────────
const helpdeskTree: NavNode = {
  id: "helpdesk", label: "Helpdesk",
  children: [
    { id: "helpdesk.overview", label: "Overview", children: [
      { id: "helpdesk.overview.dashboard", label: "Dashboard", icon: LayoutDashboard, route: "dashboard" },
    ]},
    { id: "helpdesk.tickets", label: "Tickets", children: [
      { id: "helpdesk.tickets.breached", label: "SLA Breached", icon: ShieldAlert, route: "breached" },
      { id: "helpdesk.tickets.unassigned", label: "Unassigned", icon: Inbox, route: "unassigned" },
      { id: "helpdesk.tickets.mine", label: "My Tickets", icon: Users, route: "my-tickets" },
      { id: "helpdesk.tickets.queue", label: "All Tickets", icon: Inbox, route: "queue" },
      { id: "helpdesk.tickets.unlinked", label: "Unlinked Tickets", icon: Link2Off, route: "unlinked" },
      { id: "helpdesk.tickets.escalations", label: "Escalations", icon: AlertTriangle, route: "escalations" },
      { id: "helpdesk.tickets.quotes", label: "Quotes", icon: FileText, route: "quotes", actions: ["approve"] },
      { id: "helpdesk.tickets.archived", label: "Archive", icon: Archive, route: "archived" },
    ]},
    { id: "helpdesk.reports", label: "Reports", children: [
      { id: "helpdesk.reports.analytics", label: "Analytics", icon: BarChart3, route: "analytics" },
      { id: "helpdesk.reports.quote-financials", label: "Quote Financials", icon: FileText, route: "quote-financials" },
      { id: "helpdesk.reports.recurring", label: "Recurring Tasks", icon: RefreshCw, route: "recurring-dashboard" },
    ]},
    { id: "helpdesk.settings", label: "Settings", defaultCollapsed: true, children: [
      { id: "helpdesk.settings.ticket-types", label: "Ticket Types", icon: Tags, route: "ticket-types" },
      { id: "helpdesk.settings.canned", label: "Canned Responses", icon: MessageSquare, route: "canned-responses" },
      { id: "helpdesk.settings.process-rules", label: "Process Rules", icon: BookOpen, route: "process-rules" },
      { id: "helpdesk.settings.routing", label: "Routing Rules", icon: Route, route: "routing-rules" },
      { id: "helpdesk.settings.agents", label: "Helpdesk Agents", icon: Users, route: "agents" },
    ]},
  ],
};

export const NAV_REGISTRY: Record<DashboardId, NavNode> = {
  ops: opsTree,
  bizdev: bizdevTree,
  billing: billingTree,
  performance: performanceTree,
  helpdesk: helpdeskTree,
};

/** All dashboard root trees, in order. */
export const NAV_ROOTS: NavNode[] = Object.values(NAV_REGISTRY);
